import { defineStore } from 'pinia';
import { Api } from '@/Api';

export interface Plan {
  code: string;
  name: string;
}

// Plan with display features from API (2025 format)
export interface Plan2025 {
  id: string;
  name: string;
  code: string;
  tier: string;
  rank: number;
  pricing: string;
  monthlyPrice: number;
  billingPeriod: string;
  limits: Record<string, unknown>;
  capabilities: Record<string, unknown>;
  displayFeatures: Record<string, PlanDisplayFeature>;
}

// Display feature from admin configuration
export interface PlanDisplayFeature {
  name: string;
  icon: string;
  featured?: boolean;
}

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

export interface PlansStore {
  team?: Plan[];
  personal?: Plan[];
  plans2025: Plan2025[];
  plans2025LoadedAt: number | null;
  isLoading: boolean;
}

export const usePlansStore = defineStore('plansStore', {
  state: () =>
    ({
      team: undefined,
      personal: undefined,
      plans2025: [],
      plans2025LoadedAt: null,
      isLoading: false,
    }) as PlansStore,

  actions: {
    initialize(data: any) {
      this.setTeamPlans(data.team);
      this.setPersonal(data.personal);
    },
    setTeamPlans(team: Plan[]) {
      this.team = team;
    },
    setPersonal(personal: Plan[]) {
      this.personal = personal;
    },

    async fetchPlans2025(force = false) {
      // Check cache validity
      if (!force && this.plans2025.length > 0 && this.plans2025LoadedAt) {
        if (Date.now() - this.plans2025LoadedAt < CACHE_TTL) {
          return;
        }
      }

      this.isLoading = true;
      try {
        const response = await Api.plans.listNew();
        // JSON:API response: axios wraps body in .data, then JSON:API has another .data wrapper
        const plans = response?.data?.data;
        if (!plans || !Array.isArray(plans)) {
          return;
        }
        this.plans2025 = plans.map((plan: any) => ({
          id: plan.id,
          ...plan.attributes,
        }));
        this.plans2025LoadedAt = Date.now();
      } catch {
        // Silently fail - plans2025 will remain empty/stale
      } finally {
        this.isLoading = false;
      }
    },
  },

  getters: {
    isTeam: (state: PlansStore) => (planCode: string) => {
      return !!state.team?.find(plan => plan.code === planCode);
    },
    isPersonal: (state: PlansStore) => (planCode: string) => {
      return !!state.personal?.find(plan => plan.code === planCode);
    },

    // Get display features for a specific tier
    getDisplayFeaturesByTier:
      (state: PlansStore) =>
      (tier: string): Record<string, PlanDisplayFeature> => {
        const plan = state.plans2025.find(p => p.tier === tier);
        return plan?.displayFeatures || {};
      },

    // Get plan by tier
    getPlanByTier: (state: PlansStore) => (tier: string) => {
      return state.plans2025.find(p => p.tier === tier);
    },

    // Get display features as array for a specific tier
    getDisplayFeaturesArrayByTier:
      (state: PlansStore) =>
      (tier: string): PlanDisplayFeature[] => {
        const plan = state.plans2025.find(p => p.tier === tier);
        if (!plan?.displayFeatures) {
          return [];
        }
        return Object.values(plan.displayFeatures);
      },

    // Check if a tier is a 2025 plan
    is2025Plan: (state: PlansStore) => (tier: string) => {
      return state.plans2025.some(p => p.tier === tier);
    },
  },
});
